import { CSSProperties, useState } from 'react';
import {
  addMonths, applyRangeTap, compareMonths, dayLabel, inRange, isRangeComplete, monthGrid, monthLabel, monthOf,
} from '../../../lib/almaFlow/calendar';
import type { YearMonth } from '../../../lib/almaFlow/calendar';
import { lightTap } from '../../../lib/haptics';

/**
 * RangeCalendar — one month at a time, Monday first. Tap the first day, then the
 * last day: the days in between are shaded and `onComplete` fires, so a stay is
 * two taps in one view instead of two separate date fields. Tapping the same day
 * twice is a one-day stay; tapping an earlier day moves the start; tapping again
 * after a finished range starts a new one.
 *
 * Days before `min` can't be picked. `from` / `to` are ISO dates ('' when not
 * picked yet), `onChange` reports both after every tap (`to` is '' while only the
 * first day is chosen).
 */
export interface RangeCalendarProps {
  from: string;
  to: string;
  /** Earliest pickable ISO date. */
  min: string;
  onChange: (from: string, to: string) => void;
  /** Called after the tap that finishes a range. */
  onComplete?: (from: string, to: string) => void;
  /** How far ahead the traveler can browse. Default 24 months. */
  monthsAhead?: number;
  /** Today's ISO date, marked with a small dot. */
  today?: string;
  style?: CSSProperties;
}

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const CELL = 44;
/** Every month shows six weeks, so the sheet never changes height as the traveler pages through. */
const WEEKS = 6;

export function RangeCalendar({ from, to, min, onChange, onComplete, monthsAhead = 24, today, style }: RangeCalendarProps) {
  const first = monthOf(min);
  const last = addMonths(first, monthsAhead);
  const [month, setMonth] = useState<YearMonth>(() => {
    const start = monthOf(from || min);
    return compareMonths(start, first) < 0 ? first : start;
  });
  const weeks = monthGrid(month);
  while (weeks.length < WEEKS) weeks.push(Array.from({ length: 7 }, () => null));
  const complete = isRangeComplete({ from, to });
  const single = complete && from === to;

  function tap(iso: string) {
    lightTap();
    const next = applyRangeTap({ from, to }, iso);
    onChange(next.from, next.to);
    if (next.to) onComplete?.(next.from, next.to);
  }

  const arrow = (label: string, by: -1 | 1, disabled: boolean) => (
    <button
      type="button"
      onClick={() => setMonth((m) => addMonths(m, by))}
      disabled={disabled}
      aria-label={label}
      className="sk-flow-focus"
      style={{
        width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'var(--chip-bg)', color: 'var(--text)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d={by < 0 ? 'M10 3 5 8l5 5' : 'M6 3l5 5-5 5'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {arrow('Previous month', -1, compareMonths(month, first) <= 0)}
        <div
          aria-live="polite"
          style={{ fontFamily: 'var(--font-serif)', fontSize: 17, letterSpacing: '-0.01em', color: 'var(--text)' }}
        >
          {monthLabel(month)}
        </div>
        {arrow('Next month', 1, compareMonths(month, last) >= 0)}
      </div>

      <div role="grid" aria-label={monthLabel(month)} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div role="row" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 2 }}>
          {WEEKDAYS.map((d, i) => (
            <div key={i} role="columnheader" style={{ textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--text-dim)' }}>{d}</div>
          ))}
        </div>
        {weeks.map((week, w) => (
          <div key={w} role="row" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {week.map((iso, i) => {
              if (!iso) return <div key={i} role="gridcell" style={{ height: CELL }} />;
              const disabled = iso < min;
              const isFrom = iso === from;
              const isTo = iso === to;
              const selected = isFrom || isTo;
              const inside = complete && inRange(iso, { from, to });
              // The shaded band joins the two ends: half a cell on the end days, a whole one between.
              const band = inside && !single
                ? isFrom ? 'linear-gradient(to right, transparent 50%, var(--chip-bg) 50%)'
                  : isTo ? 'linear-gradient(to left, transparent 50%, var(--chip-bg) 50%)'
                  : 'var(--chip-bg)'
                : undefined;
              return (
                <div key={i} role="gridcell" style={{ display: 'flex', justifyContent: 'center', background: band }}>
                  <button
                    type="button"
                    onClick={() => tap(iso)}
                    disabled={disabled}
                    aria-label={dayLabel(iso)}
                    aria-pressed={selected}
                    className="sk-flow-focus"
                    style={{
                      width: CELL, height: CELL, borderRadius: '50%', border: 'none',
                      background: selected ? 'var(--accent)' : 'transparent',
                      color: selected ? 'var(--on-accent)' : 'var(--text)',
                      fontFamily: 'var(--font-sans)', fontSize: 14, fontVariantNumeric: 'tabular-nums',
                      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.3 : 1,
                      transition: 'background-color .12s, color .12s', position: 'relative',
                    }}
                  >
                    {Number(iso.slice(8))}
                    {iso === today && !selected ? (
                      <span aria-hidden="true" style={{ position: 'absolute', left: '50%', bottom: 6, width: 4, height: 4, marginLeft: -2, borderRadius: '50%', background: 'var(--accent)' }} />
                    ) : null}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default RangeCalendar;
